'use client';

import { useCallback, useState } from 'react';

import { Dialog } from '@base-ui/react/dialog';
import Link from 'next/link';

import { MORE_NAV_ITEMS, NAV_GROUPS, SIGN_IN_HREF } from './nav-data';

export function MobileNav() {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger
        aria-label="Open navigation menu"
        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink transition hover:bg-state-hover md:hidden"
      >
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path
            d="M3 6h16M3 11h16M3 16h16"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 bg-canvas-dark/40 backdrop-blur-sm transition-opacity data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <Dialog.Popup className="fixed inset-x-0 top-0 z-50 max-h-screen overflow-y-auto rounded-b-3xl bg-canvas p-6 shadow-2xl transition-all data-[ending-style]:-translate-y-2 data-[ending-style]:opacity-0 data-[starting-style]:-translate-y-2 data-[starting-style]:opacity-0">
          <div className="flex items-center justify-between">
            <Dialog.Title className="font-medium text-ink text-body-lg">Menu</Dialog.Title>
            <Dialog.Close
              aria-label="Close menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink transition hover:bg-state-hover"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </Dialog.Close>
          </div>

          <nav aria-label="Primary mobile" className="mt-6">
            <ul className="space-y-1">
              {NAV_GROUPS.map((group) => (
                <li key={group.label}>
                  {group.columns ? (
                    <details className="group">
                      <summary className="flex cursor-pointer items-center justify-between rounded-2xl px-4 py-4 font-medium text-ink transition hover:bg-state-hover text-body-lg">
                        {group.label}
                        <svg
                          aria-hidden
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          className="transition-transform group-open:rotate-180"
                        >
                          <path
                            d="M3 5l4 4 4-4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </summary>
                      <div className="ml-2 mt-1 space-y-4 border-l border-line-soft pl-4">
                        {group.columns.map((column) => (
                          <div key={column.heading}>
                            <p className="px-4 pt-2 text-sm font-medium text-ink-faint">
                              {column.heading}
                            </p>
                            <ul className="mt-1 space-y-1">
                              {column.links.map((item) => (
                                <li key={item.href}>
                                  <Link
                                    href={item.href}
                                    prefetch={false}
                                    className="block rounded-xl px-4 py-2 text-ink transition hover:bg-state-hover text-body-md"
                                    onClick={close}
                                  >
                                    {item.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </details>
                  ) : (
                    <Link
                      href={group.href ?? '#'}
                      prefetch={false}
                      className="block rounded-2xl px-4 py-4 font-medium text-ink transition hover:bg-state-hover text-body-lg"
                      onClick={close}
                    >
                      {group.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="mt-4 border-t border-line-soft pt-4">
            <ul className="space-y-1">
              {MORE_NAV_ITEMS.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    prefetch={false}
                    className="block rounded-2xl px-4 py-3 text-ink-muted transition hover:bg-state-hover hover:text-ink text-body-md"
                    onClick={close}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4 border-t border-line-soft pt-4">
            <Link
              href={SIGN_IN_HREF}
              prefetch={false}
              className="block rounded-2xl px-4 py-4 font-medium text-ink transition hover:bg-state-hover text-body-lg"
              onClick={close}
            >
              Sign in
            </Link>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
